/**
 * Wake-word listener for "Forge" (also matches "Hey Forge", "OK Forge").
 * Uses the browser's SpeechRecognition API in continuous mode. Chrome
 * kills continuous SR after ~10 seconds of silence so we auto-restart
 * on onend to keep listening indefinitely.
 *
 * Public API:
 *   listenForWakeWord({ onWake, onError }) -> { stop() }
 *   fuzzyMatchesWakeWord(transcript) -> boolean
 */


function _getRec() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}


export function isWakeWordAvailable() {
  return _getRec() !== null;
}


// Regex allows "forge", "hey forge", "ok forge", "yo forge" — anywhere in transcript.
// Also accepts common mishearings that a fresh model produces on this word.
const WAKE_RE = /\b(?:hey |ok |okay |yo )?(?:forge|forg|fore ?ge|for ?j)\b/i;


export function fuzzyMatchesWakeWord(text) {
  if (!text) return false;
  return WAKE_RE.test(String(text).toLowerCase());
}


/**
 * Start continuous wake-word listening.
 *
 * @param {(matchText: string) => void} onWake  Fired once per detection. Recognition is paused
 *   before the callback so the caller can use the mic for the follow-up command; call the
 *   returned resume() when done to keep listening for the next wake word.
 * @param {(code: string, err?: unknown) => void} onError  Optional error hook.
 * @returns {{ stop(): void, isRunning(): boolean, resume(): void }}
 */
export function listenForWakeWord({ onWake, onError, language = "en-US" } = {}) {
  const Ctor = _getRec();
  if (!Ctor) {
    onError?.("not-supported");
    return { stop: () => {}, isRunning: () => false, resume: () => {} };
  }

  let rec = null;
  let stopped = false;      // hard-stop requested by caller
  let paused = false;       // temporarily stopped (during wake response)
  let lastFiredAt = 0;      // dedupe (Alexa fires once per phrase)

  const start = () => {
    if (stopped || paused) return;
    try {
      rec = new Ctor();
      rec.lang = language;
      rec.continuous = true;
      rec.interimResults = true;
      rec.maxAlternatives = 1;

      rec.onresult = (e) => {
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const t = e.results[i][0]?.transcript || "";
          if (fuzzyMatchesWakeWord(t)) {
            const now = Date.now();
            if (now - lastFiredAt < 3000) continue;    // 3s dedupe
            lastFiredAt = now;
            paused = true;
            try { rec.stop(); } catch { /* noop */ }
            try { onWake?.(t.trim()); } catch (err) { console.debug("[wake] onWake threw", err); }
            return;
          }
        }
      };

      rec.onerror = (e) => {
        const code = e?.error || "error";
        // "no-speech" and "aborted" are routine, don't spam the console
        if (code !== "no-speech" && code !== "aborted") {
          console.debug("[wake] SR error:", code);
          onError?.(code, e);
        }
      };

      rec.onend = () => {
        // Chrome auto-stops after silence — restart unless caller asked to stop
        if (!stopped && !paused) {
          setTimeout(start, 300);
        }
      };

      rec.start();
    } catch (err) {
      onError?.("start-failed", err);
    }
  };

  start();

  return {
    stop: () => {
      stopped = true;
      try { rec?.stop(); } catch { /* noop */ }
      try { rec?.abort(); } catch { /* noop */ }
      rec = null;
    },
    isRunning: () => !stopped && !paused && rec !== null,
    resume: () => {
      if (stopped) return;
      paused = false;
      // small delay so the browser releases the previous SR session
      setTimeout(start, 400);
    },
  };
}
